import React, { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import DataTable, { Column } from '../../components/DataTable/DataTable';
import { RefreshIcon, SearchIcon, TextInput } from '../../components/ui';
import { formatDate } from '../../config/appConfig';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { fetcher } from '../../services/fetcher';
import { normalizePaginatedListResponse, PaginatedApiResponse } from '../../services/paginatedList';
import { moduleConfig as spareForwardingsConfig } from '../spares/spare-forwardings/spareForwardingsModuleConfig';
import type { SpareForwarding } from '../spares/spare-forwardings/useData';
import { moduleConfig as supplyForwardingsConfig } from '../supplies/supply-forwardings/supplyForwardingsModuleConfig';
import type { SupplyForwarding } from '../supplies/supply-forwardings/useData';
import {
  resolveForwardingLocationStage,
  type ForwardingLocationStage,
} from './locationStage';
import { PurchasingModuleFilter } from '../../components/purchasing/PurchasingModuleFilter';

const FORWARDINGS_PATH = '/forwardings';

type ModuleId = 'supplies' | 'spares';

type TaggedForwarding = {
  moduleId: ModuleId;
  id: number;
  ref?: string | null;
  etaToPort?: string | null;
  vesselName?: string;
  caseRef?: string;
  caseId?: number;
  portLabel?: string;
  statusName?: string;
  priorityName?: string;
  forwarderName?: string;
  warehouseLabel?: string;
  location: ForwardingLocationStage;
  deliveriesCount?: number;
  raw: SupplyForwarding | SpareForwarding;
};

const MODULE_BADGE_META: Record<ModuleId, { label: string; className: string }> = {
  supplies: {
    label: 'Supplies',
    className:
      'border-indigo-300/80 bg-indigo-100/80 text-indigo-800 dark:border-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-200',
  },
  spares: {
    label: 'Spares',
    className:
      'border-teal-300/80 bg-teal-100/80 text-teal-800 dark:border-teal-700 dark:bg-teal-950/40 dark:text-teal-200',
  },
};

const LOCATION_BADGE_META: Record<string, string> = {
  vessel_agent_done:
    'border-emerald-300/80 bg-emerald-100/90 text-emerald-900 dark:border-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-200',
  vessel_agent:
    'border-emerald-300/60 bg-emerald-50/90 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200',
  sub_agent_done:
    'border-violet-300/80 bg-violet-100/90 text-violet-900 dark:border-violet-700 dark:bg-violet-950/50 dark:text-violet-200',
  sub_agent:
    'border-violet-300/60 bg-violet-50/90 text-violet-800 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-200',
  wh_departed:
    'border-amber-300/80 bg-amber-100/90 text-amber-900 dark:border-amber-700 dark:bg-amber-950/50 dark:text-amber-200',
  warehouse:
    'border-sky-300/80 bg-sky-100/90 text-sky-900 dark:border-sky-700 dark:bg-sky-950/50 dark:text-sky-200',
  warehouse_pending:
    'border-slate-300/80 bg-slate-100/90 text-slate-800 dark:border-slate-600 dark:bg-slate-800/60 dark:text-slate-200',
  not_started:
    'border-slate-200/80 bg-slate-50/90 text-slate-600 dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-400',
};

const formatPortLabel = (port?: { name: string; code?: string | null } | null): string => {
  if (!port?.name?.trim()) return '';
  return port.name;
};

const formatWarehouseLabel = (wh?: { name: string; code?: string | null } | null): string => {
  if (!wh?.name?.trim()) return '';
  return wh.name;
};

const formatForwarderLabel = (f?: { name: string; code?: string | null } | null): string => {
  if (!f?.name?.trim()) return '';
  return f.name;
};

const mapSupplyForwarding = (row: SupplyForwarding): TaggedForwarding => {
  const sc = row.supplyCase;
  return {
    moduleId: 'supplies',
    id: row.id,
    ref: row.ref,
    etaToPort: row.etaToPort,
    vesselName: sc?.vessel?.vesselName?.trim() || undefined,
    caseRef: sc?.ref?.trim() || undefined,
    caseId: sc?.id ?? row.caseId,
    portLabel: formatPortLabel(row.port ?? sc?.port ?? undefined),
    statusName: row.status?.name,
    priorityName: row.priority?.name,
    forwarderName: formatForwarderLabel(row.forwarder),
    warehouseLabel: formatWarehouseLabel(row.warehouseLocation),
    location: resolveForwardingLocationStage(row),
    deliveriesCount: row._count?.deliveries,
    raw: row,
  };
};

const mapSpareForwarding = (row: SpareForwarding): TaggedForwarding => {
  const sc = row.spareCase;
  return {
    moduleId: 'spares',
    id: row.id,
    ref: row.ref,
    etaToPort: row.etaToPort,
    vesselName: sc?.vessel?.vesselName?.trim() || undefined,
    caseRef: sc?.ref?.trim() || undefined,
    caseId: sc?.id ?? row.caseId,
    portLabel: formatPortLabel(row.port ?? sc?.port ?? undefined),
    statusName: row.status?.name,
    priorityName: row.priority?.name,
    forwarderName: formatForwarderLabel(row.forwarder),
    warehouseLabel: formatWarehouseLabel(row.warehouseLocation),
    location: resolveForwardingLocationStage(row),
    deliveriesCount: row._count?.deliveries,
    raw: row,
  };
};

const etaSortKey = (eta?: string | null): number => {
  if (!eta?.trim()) return Number.POSITIVE_INFINITY;
  const t = new Date(eta).getTime();
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
};

const ModuleBadge: React.FC<{ moduleId: ModuleId }> = ({ moduleId }) => {
  const meta = MODULE_BADGE_META[moduleId];
  return (
    <span
      className={cn(
        'inline-flex max-w-full items-center rounded-full border px-2.5 py-1 text-xs font-semibold tracking-tight',
        meta.className,
      )}
    >
      {meta.label}
    </span>
  );
};

const LocationBadge: React.FC<{ stage: ForwardingLocationStage }> = ({ stage }) => (
  <span
    className={cn(
      'inline-flex max-w-full items-center rounded-lg border px-2.5 py-1 text-xs font-semibold leading-snug',
      LOCATION_BADGE_META[stage.key] ?? LOCATION_BADGE_META.not_started,
    )}
    title={stage.label}
  >
    {stage.shortLabel}
  </span>
);

const TextCell: React.FC<{ value?: string | null; muted?: boolean }> = ({ value, muted }) => {
  const { colors } = useTheme();
  return (
    <span className={cn('text-sm', muted && !value ? colors.textSecondary : colors.text)}>
      {value?.trim() || '—'}
    </span>
  );
};

const ForwardingsList: React.FC = () => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { can } = useAuth();

  const canViewSupplies = can('supply_forwardings', 'view');
  const canViewSpares = can('spare_forwardings', 'view');
  const canEditSupplies = can('supply_forwardings', 'edit');
  const canEditSpares = can('spare_forwardings', 'edit');
  const showModuleChips = canViewSupplies && canViewSpares;

  const [moduleFilter, setModuleFilter] = useState<'all' | ModuleId>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);

  const suppliesUrl = canViewSupplies
    ? '/supply-forwardings?sortKey=etaToPort&sortDirection=asc'
    : null;
  const sparesUrl = canViewSpares
    ? '/spare-forwardings?sortKey=etaToPort&sortDirection=asc'
    : null;

  const {
    data: suppliesData,
    error: suppliesError,
    isLoading: suppliesLoading,
    mutate: mutateSupplies,
  } = useSWR<PaginatedApiResponse<SupplyForwarding> | SupplyForwarding[]>(
    suppliesUrl,
    fetcher,
    { revalidateOnFocus: true, revalidateOnMount: true },
  );

  const {
    data: sparesData,
    error: sparesError,
    isLoading: sparesLoading,
    mutate: mutateSpares,
  } = useSWR<PaginatedApiResponse<SpareForwarding> | SpareForwarding[]>(
    sparesUrl,
    fetcher,
    { revalidateOnFocus: true, revalidateOnMount: true },
  );

  const allRows = useMemo<TaggedForwarding[]>(() => {
    const supplies = normalizePaginatedListResponse<SupplyForwarding>(suppliesData, 1, 10_000).data;
    const spares = normalizePaginatedListResponse<SpareForwarding>(sparesData, 1, 10_000).data;
    const tagged: TaggedForwarding[] = [
      ...supplies.map(mapSupplyForwarding),
      ...spares.map(mapSpareForwarding),
    ];
    tagged.sort((a, b) => etaSortKey(a.etaToPort) - etaSortKey(b.etaToPort));
    return tagged;
  }, [suppliesData, sparesData]);

  const moduleCounts = useMemo(
    () => ({
      all: allRows.length,
      supplies: allRows.filter((r) => r.moduleId === 'supplies').length,
      spares: allRows.filter((r) => r.moduleId === 'spares').length,
    }),
    [allRows],
  );

  const filteredRows = useMemo(() => {
    let rows = allRows;
    if (moduleFilter !== 'all') {
      rows = rows.filter((r) => r.moduleId === moduleFilter);
    }
    const q = searchTerm.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      const haystack = [
        row.ref,
        row.vesselName,
        row.caseRef,
        row.caseId != null ? String(row.caseId) : '',
        row.portLabel,
        row.statusName,
        row.priorityName,
        row.forwarderName,
        row.warehouseLabel,
        row.location.label,
        row.location.shortLabel,
        row.moduleId,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [allRows, moduleFilter, searchTerm]);

  const isLoading =
    (canViewSupplies && suppliesLoading) || (canViewSpares && sparesLoading);
  const error = suppliesError || sparesError;

  const refreshAll = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        canViewSupplies ? mutateSupplies() : Promise.resolve(),
        canViewSpares ? mutateSpares() : Promise.resolve(),
      ]);
    } finally {
      setIsRefreshing(false);
    }
  }, [canViewSupplies, canViewSpares, mutateSupplies, mutateSpares]);

  const detailPath = (row: TaggedForwarding) =>
    row.moduleId === 'supplies'
      ? supplyForwardingsConfig.module.route.detail(row.id)
      : spareForwardingsConfig.module.route.detail(row.id);

  const editPath = (row: TaggedForwarding) =>
    row.moduleId === 'supplies'
      ? supplyForwardingsConfig.module.route.edit(row.id)
      : spareForwardingsConfig.module.route.edit(row.id);

  const canEditRow = (row: TaggedForwarding) =>
    row.moduleId === 'supplies' ? canEditSupplies : canEditSpares;

  const columns = useMemo<Column<TaggedForwarding>[]>(
    () => [
      {
        key: 'moduleId',
        header: 'Module',
        sortable: false,
        width: '7.5rem',
        render: (_v, row) => <ModuleBadge moduleId={row.moduleId} />,
      },
      {
        key: 'vesselName',
        header: 'Vessel',
        sortable: false,
        render: (_v, row) => <TextCell value={row.vesselName} />,
      },
      {
        key: 'caseRef',
        header: 'Case',
        sortable: false,
        maxWidth: '14rem',
        render: (_v, row) => (
          <TextCell value={row.caseRef?.trim() || (row.caseId != null ? `#${row.caseId}` : undefined)} />
        ),
      },
      {
        key: 'ref',
        header: 'Ref',
        sortable: false,
        render: (_v, row) => <TextCell value={row.ref} />,
      },
      {
        key: 'etaToPort',
        header: 'ETA to port',
        sortable: false,
        render: (_v, row) => (
          <TextCell value={row.etaToPort ? formatDate(row.etaToPort) : undefined} muted />
        ),
      },
      {
        key: 'portLabel',
        header: 'Port',
        sortable: false,
        render: (_v, row) => <TextCell value={row.portLabel} />,
      },
      {
        key: 'location',
        header: 'Location',
        sortable: false,
        minWidth: '10rem',
        render: (_v, row) => <LocationBadge stage={row.location} />,
      },
      {
        key: 'warehouseLabel',
        header: 'WH location',
        sortable: false,
        render: (_v, row) => <TextCell value={row.warehouseLabel} />,
      },
      {
        key: 'statusName',
        header: 'Status',
        sortable: false,
        render: (_v, row) => <TextCell value={row.statusName} />,
      },
      {
        key: 'forwarderName',
        header: 'Forwarder',
        sortable: false,
        render: (_v, row) => <TextCell value={row.forwarderName} />,
      },
      {
        key: 'priorityName',
        header: 'Priority',
        sortable: false,
        render: (_v, row) => <TextCell value={row.priorityName} />,
      },
    ],
    [],
  );

  const newButtonLinkClass =
    'inline-flex items-center justify-center h-9 w-9 rounded-lg border transition-colors bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700';

  const addIcon = (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  );

  const viewIcon = (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
      />
    </svg>
  );

  const editIcon = (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
      />
    </svg>
  );

  if (!canViewSupplies && !canViewSpares) {
    return (
      <div className="flex flex-col h-full min-h-0 items-center justify-center px-4">
        <p className={cn('text-sm', colors.textSecondary)}>You do not have permission to view forwardings.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-shrink-0 flex flex-row items-center gap-3 mb-3 min-w-0">
        <div className="flex flex-1 min-w-0 flex-row items-center gap-2">
          {showModuleChips && (
            <PurchasingModuleFilter
              value={moduleFilter}
              onChange={setModuleFilter}
              counts={moduleCounts}
              showSupplies={canViewSupplies}
              showSpares={canViewSpares}
              className="shrink-0"
            />
          )}
          <div className="relative min-w-0 flex-1">
            <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
            <TextInput
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search ref, vessel, case, port, status, forwarder, warehouse, location…"
              className="pl-8 h-9 text-sm w-full"
            />
          </div>
        </div>

        <div
          className={cn(
            'flex items-center gap-2 flex-shrink-0 flex-nowrap rounded-xl border py-1.5 pl-3 pr-3',
            colors.border,
            colors.surface,
          )}
        >
          <button
            type="button"
            onClick={() => {
              void refreshAll();
            }}
            disabled={isRefreshing}
            className={cn(
              'inline-flex items-center justify-center h-9 w-9 rounded-lg border transition-colors',
              colors.surface,
              colors.text,
              colors.border,
              colors.interactiveHover,
              isRefreshing && 'opacity-60',
            )}
            title="Refresh"
            aria-label="Refresh"
          >
            <RefreshIcon className={cn('w-5 h-5', isRefreshing && 'animate-spin')} />
          </button>
          {canViewSupplies && (
            <Link
              to={supplyForwardingsConfig.module.route.new}
              state={{ from: FORWARDINGS_PATH }}
              className={newButtonLinkClass}
              title="New supply forwarding"
            >
              {addIcon}
            </Link>
          )}
          {canViewSpares && (
            <Link
              to={spareForwardingsConfig.module.route.new}
              state={{ from: FORWARDINGS_PATH }}
              className={newButtonLinkClass}
              title="New spare forwarding"
            >
              {addIcon}
            </Link>
          )}
        </div>
      </div>

      <div
        className={cn(
          'flex flex-col flex-1 min-h-0 overflow-hidden rounded-xl border shadow-sm',
          'bg-white dark:bg-slate-950',
          colors.border,
        )}
      >
        <div className={cn('flex-shrink-0 flex items-center justify-between gap-2 px-4 py-2.5 border-b', colors.border)}>
          <div className="min-w-0">
            <p className="text-sm font-medium text-slate-900 dark:text-slate-100">Forwardings</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {isLoading ? 'Loading…' : `${filteredRows.length} shipment${filteredRows.length === 1 ? '' : 's'}`}
            </p>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
          {error ? (
            <div className="m-3 rounded-xl border border-rose-200 dark:border-rose-900/50 px-4 py-16 text-center">
              <p className="text-sm font-medium text-rose-700 dark:text-rose-300">Could not load forwardings</p>
              <p className="text-xs text-rose-600/80 dark:text-rose-400/80 mt-1">Try refreshing the page.</p>
            </div>
          ) : !isLoading && filteredRows.length === 0 ? (
            <div className="m-3 rounded-xl border border-dashed border-slate-200 dark:border-slate-700 px-4 py-16 text-center">
              <p className="text-sm font-medium text-slate-600 dark:text-slate-300">
                {searchTerm.trim() || moduleFilter !== 'all' ? 'No matching forwardings' : 'No forwardings yet'}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {searchTerm.trim() || moduleFilter !== 'all'
                  ? 'Adjust your search or module filter.'
                  : 'Create a supply or spare forwarding to track shipments.'}
              </p>
            </div>
          ) : (
            <DataTable
              data={filteredRows}
              columns={columns}
              loading={isLoading}
              keyExtractor={(row) => `${row.moduleId}:${row.id}`}
              onRowClick={(row) =>
                navigate(detailPath(row), { state: { from: FORWARDINGS_PATH } })
              }
              actionsPosition="left"
              actions={(row) => (
                <div className="flex items-center flex-nowrap">
                  <Link
                    to={detailPath(row)}
                    state={{ from: FORWARDINGS_PATH }}
                    className="p-1 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-lg transition-colors dark:text-indigo-400 dark:hover:bg-indigo-950/50"
                    onClick={(e) => e.stopPropagation()}
                    title="View"
                  >
                    {viewIcon}
                  </Link>
                  {canEditRow(row) && (
                    <Link
                      to={editPath(row)}
                      state={{ from: FORWARDINGS_PATH }}
                      className="p-1 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors dark:text-blue-400 dark:hover:bg-blue-950/50"
                      onClick={(e) => e.stopPropagation()}
                      title="Edit"
                    >
                      {editIcon}
                    </Link>
                  )}
                </div>
              )}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ForwardingsList;
